import { VersionCompareRow } from 'components/v2/compiler/version-compare/VersionCompareRow';
import { EpdVersionHistoryData } from 'components/v2/compiler/version-history/utils/EpdVersionHistoryDataContext';
import { CUSTOM_RESULT_KEYS, MODULE_KEYS } from 'constants/constants';
import React from 'react';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { useEpdLcaCustomResultGridData } from 'services/api/queries';
import { FieldPanelHeader } from 'styles/v2/Styles.styled';
import LcaCustomAdditionalResultsGrid from '../grids/lca-results/custom/LcaCustomAdditionalResultsGrid';
import LcaCustomBestWorstResultsGrid from '../grids/lca-results/custom/LcaCustomBestWorstResultsGrid';
import LcaCustomBiogenicResultsGrid from '../grids/lca-results/custom/LcaCustomBiogenicResultsGrid';
import { getCustomResultDescriptionRows } from '../version-history/content/VersionHistoryLcaCustomResultGrid';
import { mergeToLeftRightByIndex } from './utils/mergers';
import VersionCompareKeyValueGrid from './VersionCompareKeyValueGrid';
import { LcaImportContextProvider } from '../grids/lca-results/util/LcaImportContext';

const CompareEnvironmentPerformanceCustomSection: React.FC<{
  data: { left: EpdVersionHistoryData | null; right: EpdVersionHistoryData | null };
}> = ({ data }) => {
  const mergedCustomResults = mergeToLeftRightByIndex(data.left?.lcaCustomResults, data.right?.lcaCustomResults);
  return (
    <LcaImportContextProvider>
      {mergedCustomResults.map((resultLR, index) => {
        return (
          <React.Fragment key={index}>
            <VersionCompareRow data={resultLR} displayCondition={(x) => !!x}>
              {({ item: customResult }) => {
                const resultTypeEntry = customResult.resultType
                  ? data!.left!.dictionaries.find(
                      (d) =>
                        d.dictionaryCode === EPD_DICTIONARY_CODES.ADDITIONAL_LCA_RESULTS_TYPE &&
                        d.key === Number(customResult.resultType)
                    )
                  : null;

                const title = resultTypeEntry ? resultTypeEntry.value : 'Additional environmental performance results';

                return <FieldPanelHeader>{title}</FieldPanelHeader>;
              }}
            </VersionCompareRow>
            <VersionCompareRow data={resultLR} displayCondition={(x) => !!x}>
              {({ isLeft, item }) => (
                <VersionCompareKeyValueGrid
                  isLeft={isLeft}
                  rowsGetter={getCustomResultDescriptionRows}
                  additionalData={resultLR}
                />
              )}
            </VersionCompareRow>
            <VersionCompareRow data={resultLR} displayCondition={(x) => !!x} compareByDom>
              {({ isLeft, item: customResult, compareClassName }) => {
                const rows = useEpdLcaCustomResultGridData(customResult.id).data;
                const isTableFilled =
                  rows?.some((row: any) =>
                    MODULE_KEYS.some((key) => row[key] !== null && row[key] !== undefined && row[key] !== '')
                  ) || customResult.gridDisclaimers;
                const epdVersionId = isLeft ? data.left!.epdVersionId : data.right!.epdVersionId;

                return (
                  <>
                    {[
                      CUSTOM_RESULT_KEYS.A4C4,
                      CUSTOM_RESULT_KEYS.MODELING,
                      CUSTOM_RESULT_KEYS.ELECTRICITY,
                      CUSTOM_RESULT_KEYS.BIOGAS,
                    ].includes(customResult.resultType) &&
                      isTableFilled && (
                        <LcaCustomAdditionalResultsGrid
                          className={compareClassName}
                          epdVersionId={epdVersionId}
                          customResult={customResult}
                          disabled
                          preferEnabledAppearanceEvenWhenDisabled
                        />
                      )}
                    {customResult.resultType === CUSTOM_RESULT_KEYS.BIOGENIC && isTableFilled && (
                      <LcaCustomBiogenicResultsGrid
                        className={compareClassName}
                        epdVersionId={epdVersionId}
                        customResultId={customResult.id}
                        disabled
                        preferEnabledAppearanceEvenWhenDisabled
                      />
                    )}
                    {[CUSTOM_RESULT_KEYS.BEST, CUSTOM_RESULT_KEYS.WORST].includes(customResult.resultType) &&
                      isTableFilled && (
                        <LcaCustomBestWorstResultsGrid
                          className={compareClassName}
                          epdVersionId={epdVersionId}
                          customResultId={customResult.id}
                          disabled
                          preferEnabledAppearanceEvenWhenDisabled
                        />
                      )}
                  </>
                );
              }}
            </VersionCompareRow>
          </React.Fragment>
        );
      })}
    </LcaImportContextProvider>
  );
};

export default CompareEnvironmentPerformanceCustomSection;
